import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Package, MapPin, Sparkles, Search, HandHeart, Truck, Heart } from "lucide-react";
import * as Icons from "lucide-react";
import api, { money } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Logo from "@/components/Logo";
import { Label } from "@/components/Badges";
import { toast } from "sonner";

function Eyebrow({ children }) {
  return <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--forest)] mb-2">{children}</p>;
}

function LeafDeco({ className, color = "#8AA47F" }) {
  return (
    <svg viewBox="0 0 120 120" className={className} fill="none">
      <path d="M60 112 C 22 92 12 42 42 10 C 56 46 60 72 60 112 Z" fill={color} />
      <path d="M60 112 C 98 92 108 42 78 10 C 64 46 60 72 60 112 Z" fill={color} opacity="0.82" />
      <path d="M60 108 L60 28" stroke="#5E7A5A" strokeWidth="2.5" opacity="0.45" />
    </svg>
  );
}

const LOCAL_CHIPS = {
  coffee: { label: "Coffee & Drinks", icon: "Coffee", color: "#C4703C", bg: "#F7E7D6" },
  "wellness-local": { label: "Healthy Eats", icon: "Leaf", color: "#3F5C46", bg: "#E7EFE4" },
  restaurants: { label: "Restaurants", icon: "UtensilsCrossed", color: "#C4703C", bg: "#F7E7D6" },
  "fitness-local": { label: "Wellness", icon: "Flower2", color: "#7C6BAF", bg: "#EEE9F7" },
  hair: { label: "Beauty", icon: "Scissors", color: "#C4703C", bg: "#F7E7D6" },
};
function localChip(o) {
  if (o.business === "rise-bakery" || o.slug === "free-pastry") return { label: "Bakery", icon: "Croissant", color: "#C4703C", bg: "#F7E7D6" };
  return LOCAL_CHIPS[o.business_info?.category] || { label: "Local", icon: "Tag", color: "#3F5C46", bg: "#E7EFE4" };
}

function HandNote({ className, children, color = "#6E8B6A" }) {
  return <span className={`font-hand text-2xl leading-[1] hidden lg:block absolute z-10 pointer-events-none text-center ${className}`} style={{ color }}>{children}</span>;
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [ugc, setUgc] = useState([]);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    api.get("/products?sort=trending&limit=8").then((r) => setProducts(r.data));
    api.get("/local/offers?sort=distance").then((r) => setOffers(r.data.slice(0, 4)));
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
    api.get("/ugc").then((r) => setUgc(r.data));
  }, []);

  const join = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email, phone: phone || null }); toast.success("You're on the list! 🎉"); setEmail(""); setPhone(""); }
    catch { toast.error("Please enter a valid email."); }
  };

  return (
    <div>
      {/* HERO + Discovery share one earthy background */}
      <div className="relative overflow-hidden pb-14">
        <div className="absolute inset-0 -z-0">
          <img src="/hero-bg.png" alt="" className="w-full h-full object-cover" />
        </div>
        <section className="relative">
        <div className="absolute top-1/2 left-2 z-20 hidden lg:block -rotate-6 text-center pointer-events-none">
          <span className="font-hand text-2xl leading-[1] block" style={{ color: "#6E8B6A" }}>Support<br />small<br />brands</span>
          <Heart size={16} className="mx-auto mt-1 fill-[#C4703C] text-[#C4703C]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Logo className="h-28 sm:h-36 lg:h-44 mt-8 -mb-4 -ml-10" />
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.92] text-[#24301F]">
              Try something<br />new <span className="text-gradient-friib">For Friib.</span>
            </h1>
            <p className="mt-6 text-lg text-[#4A5544] max-w-md">
              Discover products from brands looking for their next customer. The product is on them. You just cover shipping.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" data-testid="hero-shop-btn" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white text-base transition-transform hover:scale-[1.03] shadow-lg" style={{ background: "var(--forest)", boxShadow: "0 12px 28px -8px rgba(63,92,70,.45)" }}>
                Shop Friibs <ArrowRight size={18} />
              </Link>
              <Link to="/local" data-testid="hero-local-btn" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-stone-900 bg-white friib-shadow text-base transition-transform hover:scale-[1.03]">
                <MapPin size={18} style={{ color: "var(--mint)" }} /> Friib Local
              </Link>
            </div>
            <span className="mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/90 friib-shadow text-xs font-bold text-stone-600">
              <Sparkles size={14} className="text-[var(--friib)]" /> No subscription. No bidding. Just great products.
            </span>
          </motion.div>

          {/* Floating visual */}
          <div className="relative h-[480px] hidden lg:block">
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5 }}
              className="absolute top-0 left-2 z-30 text-right">
              <span className="font-hand text-3xl leading-[1.05] block" style={{ color: "#6E8B6A" }}>Great<br />products<br />find you.</span>
              <svg width="70" height="60" viewBox="0 0 70 60" className="mt-1 ml-auto" fill="none">
                <path d="M8 6 C 40 14, 58 24, 50 48" stroke="#6E8B6A" strokeWidth="3.5" strokeLinecap="round" />
                <path d="M40 40 L50 50 L60 40" stroke="#6E8B6A" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </motion.div>

            <span className="absolute top-8 right-24 w-9 h-2.5 rounded-full rotate-[35deg]" style={{ background: "#E08A3E" }} />
            <span className="absolute top-16 right-14 w-6 h-2.5 rounded-full rotate-[70deg]" style={{ background: "#E08A3E" }} />
            <span className="absolute top-1/2 -right-2 w-10 h-2.5 rounded-full -rotate-[25deg]" style={{ background: "#8AA47F" }} />
            <span className="absolute bottom-16 right-6 w-9 h-2.5 rounded-full rotate-[20deg]" style={{ background: "#8AA47F" }} />
            <div className="absolute bottom-0 right-0 z-20 rotate-[8deg] text-center">
              <span className="font-hand text-2xl leading-[1] block" style={{ color: "#6E8B6A" }}>Good<br />things<br />near you</span>
            </div>

            {products.slice(0, 3).map((p, i) => {
              const pos = [
                "bottom-4 left-0 -rotate-[7deg] w-56 z-10",
                "top-28 left-32 rotate-[3deg] w-56 z-20",
                "top-0 right-0 rotate-[7deg] w-60 z-[25]",
              ][i];
              return (
                <motion.div key={p.slug}
                  initial={{ opacity: 0, y: 30, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 0.2 + i * 0.14 }}
                  className={`absolute bg-white rounded-3xl border-4 border-white friib-shadow-hover p-3 ${pos}`}>
                  <div className="relative">
                    <img src={p.images[0]} alt={p.name} className="w-full h-36 object-cover rounded-2xl" />
                    <span className="absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-full bg-white/90 friib-shadow"><Heart size={15} className="text-stone-300" /></span>
                  </div>
                  <p className="mt-2 text-xs font-bold text-stone-500">{p.brand_name}</p>
                  <p className="font-display font-bold text-sm text-stone-900 line-clamp-1">{p.name}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-extrabold text-white" style={{ background: "var(--mint)" }}>FREE</span>
                    <span className="text-[11px] font-semibold text-stone-400">+ {money(p.shipping)} S&H</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DUAL DISCOVERY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-5">
          {[
            { to: "/shop", icon: Package, title: "Friibs Delivered", copy: "Discover products from brands and just cover shipping.", cta: "Shop Products", bg: "linear-gradient(120deg,#C4703C 0%,#D98A4E 100%)" },
            { to: "/local", icon: MapPin, title: "Friibs Near You", copy: "Find free food, experiences, services and more around you.", cta: "Explore Nearby", bg: "linear-gradient(120deg,#5E7A5A 0%,#7B9873 100%)" },
          ].map((c) => (
            <Link key={c.to} to={c.to} data-testid={`discovery-${c.title.toLowerCase().replace(/\s/g, "-")}`}
              className="group relative overflow-hidden rounded-3xl p-8 lg:p-10 text-white transition-transform hover:scale-[1.01] min-h-[220px] flex flex-col justify-end" style={{ background: c.bg }}>
              <c.icon className="absolute -right-4 -bottom-6 opacity-15" size={190} strokeWidth={1.5} />
              <c.icon size={40} className="mb-4 relative z-10" />
              <h3 className="font-display text-3xl font-extrabold mb-1 relative z-10 flex items-center gap-2">{c.title} <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" /></h3>
              <p className="text-white/90 max-w-xs relative z-10">{c.copy}</p>
            </Link>
          ))}
        </div>
      </section>
      </div>

      {/* TRENDING */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div><Eyebrow>Fresh Friib Drops</Eyebrow><h2 className="font-display text-3xl lg:text-4xl font-bold text-stone-900">Trending Friibs</h2></div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 font-bold text-stone-600 hover:text-[var(--friib)]">View all <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {products.slice(0, 8).map((p, i) => <ProductCard key={p.slug} p={p} index={i} />)}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <Eyebrow>Browse</Eyebrow><h2 className="font-display text-3xl lg:text-4xl font-bold text-stone-900 mb-8">Shop by Category</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {cats.map((c) => {
            const Icon = Icons[c.icon] || Icons.Tag;
            return (
              <Link key={c.slug} to={`/shop?category=${c.slug}`} data-testid={`category-${c.slug}`}
                className="group flex flex-col items-center gap-2 p-5 rounded-2xl bg-white friib-shadow hover:friib-shadow-hover transition-all hover:-translate-y-1 border border-stone-100">
                <span className="w-12 h-12 grid place-items-center rounded-full" style={{ background: "var(--friib-light)" }}>
                  <Icon size={22} style={{ color: "var(--friib)" }} />
                </span>
                <span className="text-sm font-bold text-stone-700">{c.name}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-stone-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-center mb-12"><Eyebrow>How Friib Works</Eyebrow><h2 className="font-display text-3xl lg:text-4xl font-bold text-stone-900">Try it. Love it. Come back.</h2><p className="text-stone-500 mt-2">No membership required.</p></div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Search, n: "1", t: "Discover", d: "Browse products from brands you love and brands you've never tried." },
              { icon: HandHeart, n: "2", t: "Claim", d: "The brand covers the product. You cover shipping. That's it." },
              { icon: Truck, n: "3", t: "Find your new favorite", d: "Try it at home. If you love it, come back for more." },
            ].map((s) => (
              <div key={s.n} className="relative p-8 rounded-3xl bg-stone-50 border border-stone-100">
                <span className="absolute -top-4 left-8 w-10 h-10 grid place-items-center rounded-full text-white font-display font-extrabold" style={{ background: "var(--ink)" }}>{s.n}</span>
                <s.icon size={32} style={{ color: "var(--friib)" }} className="mb-4 mt-2" />
                <h3 className="font-display text-xl font-bold text-stone-900 mb-2">{s.t}</h3>
                <p className="text-stone-500">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCAL + BRANDS on shared earthy background */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 -z-0"><img src="/local-bg.png" alt="" className="w-full h-full object-cover" /></div>

        <HandNote className="top-40 left-2 -rotate-6">Support<br />local<br /><Heart size={15} className="inline fill-[#C4703C] text-[#C4703C]" /></HandNote>
        <HandNote className="top-6 right-8 rotate-[8deg]">Good<br />things<br />near you</HandNote>
        <HandNote className="top-1/3 right-2 -rotate-3" color="#C99060">Stronger<br />Happier<br />You</HandNote>
        <HandNote className="bottom-40 right-6 rotate-[6deg]">More<br />good stuff<br />near you</HandNote>
        <HandNote className="bottom-10 right-10 -rotate-6">Local<br />brands<br />brighter<br />days <Heart size={14} className="inline fill-[#C4703C] text-[#C4703C]" /></HandNote>

        {/* LOCAL PREVIEW */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14">
          <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
            <div>
              <Eyebrow>Friib Local</Eyebrow>
              <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-[#24301F] leading-tight">Friibs <span style={{ color: "var(--mint)" }}>near you</span></h2>
              <p className="text-[#4A5544] mt-1">Great local offers. Real places. Happening near you.</p>
            </div>
            <Link to="/local" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white friib-shadow font-bold text-[var(--mint)] hover:scale-[1.03] transition-transform"><MapPin size={16} /> Explore nearby <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {offers.map((o, i) => {
              const chip = localChip(o); const CIcon = Icons[chip.icon] || Icons.Tag;
              return (
                <motion.div key={o.slug} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}>
                  <Link to={`/local/${o.slug}`} data-testid={`local-card-${o.slug}`} className="group bg-white rounded-3xl overflow-hidden friib-shadow hover:friib-shadow-hover transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={o.image} alt={o.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                      <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">{(o.labels || []).slice(0, 2).map((l) => <Label key={l}>{l}</Label>)}</div>
                      <span className="absolute top-3 right-3 w-9 h-9 grid place-items-center rounded-full bg-white/90 friib-shadow"><Heart size={16} className="text-stone-400" /></span>
                      <div className="absolute bottom-3 left-3 flex items-center gap-1 text-white text-xs font-semibold"><MapPin size={13} /> {o.distance} mi away</div>
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500 truncate">{o.business_name}</span>
                        <span className="flex items-center gap-1 text-sm font-bold text-stone-700 shrink-0"><Icons.Star size={13} className="fill-amber-400 text-amber-400" /> {o.business_info?.rating} <span className="text-stone-400 font-normal text-xs">({o.business_info?.review_count})</span></span>
                      </div>
                      <h3 className="font-display text-lg font-extrabold text-stone-900 leading-snug">{o.title}</h3>
                      <p className="text-sm text-stone-500 mt-1 mb-3">Up to {money(o.normal_value)} value · {o.purchase_requirement}</p>
                      <div className="flex items-center justify-between mb-3 mt-auto">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: chip.bg, color: chip.color }}><CIcon size={13} /> {chip.label}</span>
                        <span className="text-xs font-semibold text-stone-400">{o.inventory_remaining} left</span>
                      </div>
                      <span className="w-full py-3 rounded-full font-bold text-white text-sm flex items-center justify-center gap-2 group-hover:brightness-110 transition-all" style={{ background: "var(--forest)" }}><Icons.Store size={15} /> Claim Friib <ArrowRight size={15} /></span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* BRANDS */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20">
          <Eyebrow>Discover Brands</Eyebrow>
          <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-[#24301F]">Brands worth trying</h2>
          <p className="text-[#4A5544] mt-1 mb-7">Local favorites, hidden gems, and new brands — all in one place.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {brands.map((b) => (
              <Link key={b.slug} to={`/shop?brand=${b.slug}`} data-testid={`brand-${b.slug}`}
                className="flex items-center gap-3 p-3.5 rounded-2xl bg-white friib-shadow hover:friib-shadow-hover transition-all hover:-translate-y-0.5">
                <span className="w-12 h-12 grid place-items-center rounded-full text-white font-display font-extrabold text-lg shrink-0" style={{ background: b.color }}>{b.name[0]}</span>
                <div className="min-w-0 flex-1"><p className="font-bold text-stone-900 truncate">{b.name}</p><p className="text-xs text-stone-400">{b.friib_count} Friibs live</p></div>
                <Icons.ChevronRight size={18} className="text-stone-300 shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      </div>

      {/* SOCIAL PROOF */}
      <section className="bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <Eyebrow>Loved by shoppers</Eyebrow><h2 className="font-display text-3xl lg:text-4xl font-bold text-stone-900 mb-8">Real Friib finds</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {ugc.map((u, i) => (
              <div key={i} className="rounded-3xl overflow-hidden bg-stone-50 border border-stone-100 friib-shadow">
                <img src={u.photo} alt={u.name} className="w-full h-56 object-cover" />
                <div className="p-6">
                  <p className="text-stone-700 font-medium mb-3">"{u.text}"</p>
                  <p className="text-sm font-bold text-stone-900">{u.name} <span className="text-stone-400 font-normal">{u.handle}</span></p>
                  <p className="text-xs text-[var(--friib)] font-semibold mt-1">{u.product}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EMAIL CLUB */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-0"><img src="/club-bg.webp" alt="" className="w-full h-full object-cover" /></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
          <div className="rounded-[2rem] bg-white/55 backdrop-blur-md border border-white/70 shadow-[0_20px_60px_-20px_rgba(63,92,70,.25)] p-8 lg:p-12 flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1 w-full text-center lg:text-left">
              <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
                <span className="text-[#24301F]">Never miss a </span>
                <span style={{ color: "var(--friib)" }}>Fri</span><span style={{ color: "var(--mint)" }}>ib</span>
                <span style={{ color: "var(--mint)" }}> Drop</span>
              </h2>
              <p className="text-[#4A5544] text-lg mt-2 mb-6">Be the first to know when new products go live.</p>
              <form onSubmit={join} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto lg:mx-0" data-testid="newsletter-form">
                <div className="relative flex-1">
                  <Icons.Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="newsletter-email"
                    placeholder="you@email.com" className="w-full pl-11 pr-4 py-3.5 rounded-full bg-white shadow-sm border border-white text-stone-900 outline-none focus:ring-2 focus:ring-[var(--forest)]/30" />
                </div>
                <button data-testid="newsletter-submit" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-bold text-white transition-transform hover:scale-[1.03] shrink-0" style={{ background: "var(--friib)" }}>Join Friib <ArrowRight size={18} /></button>
              </form>
              <p className="mt-3 text-xs text-[#7A836F]">No spam. Unsubscribe anytime.</p>
            </div>
            <div className="shrink-0">
              <img src="/club-envelope.png" alt="Friib drop envelope" className="w-52 lg:w-64 h-auto mix-blend-multiply" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
